import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Eye, Palette, Target, Users, Check } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

export default function AboutPage() {
  const { t } = useLanguage();
  const valueIcons = [Eye, Palette, Target, Users];

  return (
    <main className="bg-white" data-testid="about-page">
      {/* Header */}
      <section className="relative overflow-hidden pt-40 pb-20 sm:pt-52 sm:pb-28 bg-[#fbd9e5]">
        <div aria-hidden className="pointer-events-none absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-white/40 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="text-xs sm:text-sm font-bold uppercase tracking-[0.3em] text-[#3e2723]/60"
          >
            {t.about.eyebrow}
          </motion.p>
          <motion.h1
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="visible"
            className="mt-6 font-display text-5xl sm:text-7xl lg:text-8xl font-black tracking-tighter leading-[0.95] text-[#3e2723] max-w-5xl"
          >
            {t.about.title}
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="visible"
            className="mt-8 max-w-3xl text-xl sm:text-2xl font-medium text-[#3e2723]/80 leading-snug"
          >
            {t.about.lead}
          </motion.p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 grid gap-14 lg:grid-cols-12 items-start">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-5"
          >
            <div className="relative overflow-hidden rounded-[2.5rem] aspect-[4/5]">
              <img
                src="https://images.pexels.com/photos/17582358/pexels-photo-17582358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Founder of Social Destiny"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#3e2723]/80 via-[#3e2723]/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 text-[#fbd9e5]">
                <span className="text-[11px] uppercase tracking-[0.3em] text-[#fbd9e5]/70">
                  {t.about.team_title}
                </span>
                <p className="mt-2 font-display text-2xl sm:text-3xl font-bold tracking-tight">
                  {t.about.team_name}
                </p>
                <p className="text-sm text-[#fbd9e5]/75">{t.about.team_role}</p>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-7 space-y-6">
            <motion.p
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              className="text-xl sm:text-2xl font-display font-semibold text-[#3e2723] leading-snug"
            >
              {t.about.p1}
            </motion.p>
            <motion.p
              variants={fadeUp}
              custom={1}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              className="text-base sm:text-lg text-[#7a5c55] leading-relaxed"
            >
              {t.about.p2}
            </motion.p>
            <motion.p
              variants={fadeUp}
              custom={2}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              className="text-base sm:text-lg text-[#7a5c55] leading-relaxed"
            >
              {t.about.team_bio}
            </motion.p>

            <motion.div
              variants={fadeUp}
              custom={3}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              className="pt-4"
            >
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-[#3e2723] text-[#fbd9e5] px-6 py-3.5 text-[14px] font-semibold hover:bg-[#2a1a17] hover:-translate-y-0.5 transition-all duration-300"
                data-testid="about-cta"
              >
                {t.nav.cta}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Image + Text section */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 grid gap-12 lg:grid-cols-12 items-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-6"
          >
            <div className="relative overflow-hidden rounded-[2.5rem] aspect-[4/3]">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80"
                alt="Social Destiny team collaboration"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#fbd9e5]/0 via-transparent to-[#3e2723]/30" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3 rounded-2xl bg-white/90 backdrop-blur-xl border border-white p-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#3e2723] text-[#fbd9e5] font-display font-bold">
                  SD
                </span>
                <p className="text-[13px] font-semibold text-[#3e2723] leading-tight">
                  Strategie. Producție. Performanță.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-6"
          >
            <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#3e2723] leading-[1.02]">
              {t.about.section_image_title}
            </h2>
            <p className="mt-6 text-lg text-[#7a5c55] leading-relaxed">
              {t.about.section_image_body}
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {t.about.section_image_points.map((point, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 rounded-2xl border border-[#3e2723]/10 bg-[#fbd9e5]/30 px-4 py-3 text-[14px] font-medium text-[#3e2723]"
                >
                  <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#3e2723] text-[#fbd9e5]">
                    <Check className="h-3 w-3" />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 sm:py-32 bg-[#faf8f8]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#3e2723] max-w-2xl"
          >
            {t.about.values_title}
          </motion.h2>
          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {t.about.values.map((v, i) => {
              const Icon = valueIcons[i];
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  className="group rounded-[1.75rem] bg-white border border-[#3e2723]/8 p-7 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(62,39,35,0.08)] transition-all duration-500"
                  data-testid={`value-${i}`}
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#fbd9e5] text-[#3e2723] group-hover:bg-[#3e2723] group-hover:text-[#fbd9e5] transition-colors">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-8 font-display text-xl font-bold tracking-tight text-[#3e2723]">
                    {v.title}
                  </h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-[#7a5c55]">{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
}
